/**
 * Host Global - Property Rental Management System
 * Super Admin - Inspections Review Page
 * View inspection details and approve/reject properties
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// API
import { superAdminAPI } from "utils/api";

// Icons
import Icon from "@mui/material/Icon";

function SuperAdminInspections() {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [filteredInspections, setFilteredInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // "approve" or "reject"
  const [notes, setNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [price, setPrice] = useState("");
  const [profitPercentage, setProfitPercentage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [detailsTab, setDetailsTab] = useState(0);
  const [statusFilter, setStatusFilter] = useState("Inspection_Completed");

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [inspectorFilter, setInspectorFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    approved: 0,
    rejected: 0,
  });

  useEffect(() => {
    fetchInspections();
  }, [statusFilter]);

  // Filter inspections based on search and filters
  // Always filter from the original inspections list
  useEffect(() => {
    // Start with the original inspections list (not filteredInspections)
    let filtered = [...inspections];

    // Search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (property) =>
          (typeof property.title === "string"
            ? property.title
            : property.title?.en || property.title?.ar || ""
          )
            .toLowerCase()
            .includes(searchLower) ||
          (typeof property.address === "string"
            ? property.address
            : property.address?.en || property.address?.ar || ""
          )
            .toLowerCase()
            .includes(searchLower) ||
          getCityString(property).toLowerCase().includes(searchLower) ||
          property.owner?.fullName?.toLowerCase().includes(searchLower) ||
          property.assignedInspector?.fullName?.toLowerCase().includes(searchLower)
      );
    }

    // Inspector filter
    if (inspectorFilter !== "all") {
      filtered = filtered.filter(
        (property) =>
          property.assignedInspector?._id === inspectorFilter ||
          property.assignedInspector?.id === inspectorFilter
      );
    }

    // City filter
    if (cityFilter !== "all") {
      filtered = filtered.filter((property) => getCityString(property) === cityFilter);
    }

    // Type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter((property) => property.propertyType === typeFilter);
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      filtered = filtered.filter((property) => {
        if (!property.inspectionFindings?.inspectedAt) return false;
        const inspectedDate = new Date(property.inspectionFindings.inspectedAt);
        switch (dateFilter) {
          case "today":
            return inspectedDate >= today;
          case "week":
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return inspectedDate >= weekAgo;
          case "month":
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return inspectedDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    setFilteredInspections(filtered);
  }, [inspections, searchTerm, inspectorFilter, cityFilter, typeFilter, dateFilter]);

  const fetchInspections = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });

      let response;
      if (statusFilter === "Inspection_Completed") {
        response = await superAdminAPI.getPropertiesReadyForApproval();
      } else if (statusFilter === "Second_Inspection_Completed") {
        response = await superAdminAPI.getPropertiesByStatus("Second_Inspection_Completed");
      } else {
        response = await superAdminAPI.getPropertiesByStatus(statusFilter);
      }

      const properties = response.data || response.properties || [];
      setInspections(properties);
      setFilteredInspections(properties);

      // Statistics: first inspection ready, second inspection ready, approved, rejected
      const [readyForApproval, secondReady, approved, rejected] = await Promise.all([
        superAdminAPI.getPropertiesReadyForApproval().catch(() => ({ data: [] })),
        superAdminAPI
          .getPropertiesByStatus("Second_Inspection_Completed")
          .catch(() => ({ data: [] })),
        superAdminAPI.getPropertiesByStatus("Approved").catch(() => ({ data: [] })),
        superAdminAPI.getPropertiesByStatus("Rejected").catch(() => ({ data: [] })),
      ]);

      const readyList = readyForApproval.data || readyForApproval.properties || [];
      const secondReadyList = secondReady.data || secondReady.properties || [];
      const approvedList = approved.data || approved.properties || [];
      const rejectedList = rejected.data || rejected.properties || [];
      const pendingCount = readyList.length + secondReadyList.length;
      const totalCompleted = pendingCount + approvedList.length + rejectedList.length;

      setStats({
        total: totalCompleted,
        pending: pendingCount,
        completed: totalCompleted,
        approved: approvedList.length,
        rejected: rejectedList.length,
      });
    } catch (error) {
      console.error("Error fetching inspections:", error);
      setMessage({
        type: "error",
        text: "Failed to load inspections. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (property) => {
    setSelectedInspection(property);
    setDetailsDialogOpen(true);
    setDetailsTab(0);
  };

  const handleOpenAction = (property, type) => {
    setSelectedInspection(property);
    setActionType(type);
    setNotes("");
    setRejectReason("");
    setPrice("");
    setProfitPercentage("");
    setActionDialogOpen(true);
  };

  const handleSubmitAction = async () => {
    if (!selectedInspection) return;

    if (actionType === "reject" && !rejectReason.trim()) {
      setMessage({
        type: "error",
        text: "Rejection reason is required.",
      });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      let response;
      const isSecondInspection = selectedInspection.status === "Second_Inspection_Completed";
      if (actionType === "approve") {
        if (isSecondInspection) {
          response = await superAdminAPI.approveSecondInspection(
            selectedInspection._id || selectedInspection.id
          );
        } else {
          response = await superAdminAPI.approveProperty(
            selectedInspection._id || selectedInspection.id,
            notes.trim() || undefined
          );
        }
      } else {
        response = await superAdminAPI.rejectProperty(
          selectedInspection._id || selectedInspection.id,
          rejectReason.trim()
        );
      }

      if (response.success) {
        setMessage({
          type: "success",
          text: `Property ${actionType === "approve" ? "approved" : "rejected"} successfully.`,
        });
        setActionDialogOpen(false);
        setDetailsDialogOpen(false);
        setSelectedInspection(null);
        setNotes("");
        setRejectReason("");
        await fetchInspections();
      }
    } catch (error) {
      console.error(`Error ${actionType}ing property:`, error);
      setMessage({
        type: "error",
        text: error.message || `Failed to ${actionType} property. Please try again.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Normalize city to string (backend may return { en, ar } or string)
  const getCityString = (property) => {
    if (!property) return "";
    const c = property.city;
    if (!c) return "";
    if (typeof c === "string") return c;
    if (typeof c === "object" && c !== null) return c.en || c.ar || "";
    return "";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "success";
      case "Pending":
        return "warning";
      case "Inspection":
        return "info";
      case "Inspection_Completed":
      case "Second_Inspection_Completed":
        return "info";
      case "Rejected":
        return "error";
      default:
        return "secondary";
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="bold">
            Inspections Review
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Review completed inspections and approve or reject properties
          </MDTypography>
        </MDBox>

        {/* Statistics Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              title="Total Completed"
              count={stats.total}
              icon="task_alt"
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              title="Pending Review"
              count={stats.pending}
              icon="pending"
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              title="Approved"
              count={stats.approved}
              icon="check_circle"
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              title="Rejected"
              count={stats.rejected}
              icon="cancel"
              color="error"
            />
          </Grid>
        </Grid>

        {/* Message Alert */}
        {message.text && (
          <MDBox mb={3}>
            <Alert severity={message.type} onClose={() => setMessage({ type: "", text: "" })}>
              {message.text}
            </Alert>
          </MDBox>
        )}

        {/* Search and Filters */}
        <Card sx={{ mb: 3, p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by property title, address, owner, or inspector..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>search</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="Inspection_Completed">
                    First Inspection (Ready for Review)
                  </MenuItem>
                  <MenuItem value="Second_Inspection_Completed">
                    Second Inspection (Ready for Review)
                  </MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* City Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>City</InputLabel>
                <Select
                  value={cityFilter}
                  label="City"
                  onChange={(e) => setCityFilter(e.target.value)}
                >
                  <MenuItem value="all">All Cities</MenuItem>
                  {[...new Set(inspections.map((p) => getCityString(p)).filter(Boolean))].map(
                    (city) => (
                      <MenuItem key={city} value={city}>
                        {city}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Type Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {[...new Set(inspections.map((p) => p.propertyType).filter(Boolean))].map(
                    (type) => (
                      <MenuItem key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Date Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Date</InputLabel>
                <Select
                  value={dateFilter}
                  label="Date"
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">Last Week</MenuItem>
                  <MenuItem value="month">Last Month</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Active Filters Display */}
          {(searchTerm ||
            inspectorFilter !== "all" ||
            cityFilter !== "all" ||
            typeFilter !== "all" ||
            dateFilter !== "all") && (
            <MDBox mt={2} display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <MDTypography variant="caption" color="text.secondary" mr={1}>
                Active Filters:
              </MDTypography>
              {searchTerm && (
                <Chip
                  label={`Search: "${searchTerm}"`}
                  onDelete={() => setSearchTerm("")}
                  size="small"
                  color="primary"
                />
              )}
              {cityFilter !== "all" && (
                <Chip
                  label={`City: ${cityFilter}`}
                  onDelete={() => setCityFilter("all")}
                  size="small"
                />
              )}
              {typeFilter !== "all" && (
                <Chip
                  label={`Type: ${typeFilter}`}
                  onDelete={() => setTypeFilter("all")}
                  size="small"
                />
              )}
              {dateFilter !== "all" && (
                <Chip
                  label={`Date: ${dateFilter}`}
                  onDelete={() => setDateFilter("all")}
                  size="small"
                />
              )}
              <MDButton
                size="small"
                variant="text"
                onClick={() => {
                  setSearchTerm("");
                  setInspectorFilter("all");
                  setCityFilter("all");
                  setTypeFilter("all");
                  setDateFilter("all");
                }}
              >
                Clear All
              </MDButton>
            </MDBox>
          )}

          {/* Results Count */}
          <MDBox mt={2}>
            <MDTypography variant="body2" color="text.secondary">
              Showing {filteredInspections.length} of {inspections.length} inspections
            </MDTypography>
          </MDBox>
        </Card>

        {/* Inspections List */}
        {loading ? (
          <MDBox display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </MDBox>
        ) : inspections.length === 0 ? (
          <Card>
            <MDBox p={6} textAlign="center">
              <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>inbox</Icon>
              <MDTypography variant="h6" color="text.secondary" mb={1}>
                No Inspections Found
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                There are no completed inspections ready for review.
              </MDTypography>
            </MDBox>
          </Card>
        ) : filteredInspections.length === 0 ? (
          <Card>
            <MDBox p={6} textAlign="center">
              <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>search_off</Icon>
              <MDTypography variant="h6" color="text.secondary" mb={1}>
                No Inspections Match Your Filters
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" mb={2}>
                Try adjusting your search or filter criteria.
              </MDTypography>
              <MDButton
                variant="outlined"
                onClick={() => {
                  setSearchTerm("");
                  setInspectorFilter("all");
                  setCityFilter("all");
                  setTypeFilter("all");
                  setDateFilter("all");
                }}
              >
                Clear Filters
              </MDButton>
            </MDBox>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {filteredInspections.map((property) => (
              <Grid item xs={12} md={6} lg={4} key={property._id || property.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  {/* Property Image */}
                  {property.images && property.images.length > 0 ? (
                    <MDBox
                      position="relative"
                      height="200px"
                      sx={{
                        backgroundImage: `url(${property.images[0]})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    >
                      <MDBox
                        position="absolute"
                        top={12}
                        right={12}
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-end"
                        gap={0.5}
                      >
                        {[
                          "Inspection_Completed",
                          "Inspection_Offer",
                          "Second_Inspection_Completed",
                        ].includes(property.status) && (
                          <Chip
                            label={
                              property.status === "Second_Inspection_Completed"
                                ? "Second Inspection"
                                : "First Inspection"
                            }
                            color={
                              property.status === "Second_Inspection_Completed"
                                ? "secondary"
                                : "info"
                            }
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                        <Chip
                          label={property.status?.replace(/_/g, " ") || "Unknown"}
                          color={getStatusColor(property.status)}
                          size="small"
                        />
                      </MDBox>
                    </MDBox>
                  ) : (
                    <MDBox
                      height="200px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      bgcolor="grey.200"
                    >
                      <Icon sx={{ fontSize: "3rem", color: "grey.400" }}>home</Icon>
                    </MDBox>
                  )}

                  <MDBox p={3} flex={1} display="flex" flexDirection="column">
                    {/* Property Title */}
                    <MDTypography variant="h6" fontWeight="bold" mb={1}>
                      {property.title?.en || property.title || "Untitled Property"}
                    </MDTypography>

                    {/* Property Details */}
                    <MDBox mb={2}>
                      <MDBox display="flex" alignItems="center" mb={0.5}>
                        <Icon fontSize="small" sx={{ mr: 1, color: "text.secondary" }}>
                          location_on
                        </Icon>
                        <MDTypography variant="body2" color="text">
                          {property.address?.en || property.address || ""}
                          {property.city?.en || property.city
                            ? `, ${property.city?.en || property.city}`
                            : ""}
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="center" mb={0.5}>
                        <Icon fontSize="small" sx={{ mr: 1, color: "text.secondary" }}>
                          category
                        </Icon>
                        <MDTypography variant="body2" color="text">
                          {property.propertyType
                            ? property.propertyType.charAt(0).toUpperCase() +
                              property.propertyType.slice(1)
                            : "N/A"}
                        </MDTypography>
                      </MDBox>
                      {property.assignedInspector && (
                        <MDBox display="flex" alignItems="center" mb={0.5}>
                          <Icon fontSize="small" sx={{ mr: 1, color: "text.secondary" }}>
                            person
                          </Icon>
                          <MDTypography variant="body2" color="text">
                            Inspector: {property.assignedInspector.fullName || "N/A"}
                          </MDTypography>
                        </MDBox>
                      )}
                      {property.inspectionFindings?.inspectedAt && (
                        <MDBox display="flex" alignItems="center">
                          <Icon fontSize="small" sx={{ mr: 1, color: "text.secondary" }}>
                            event
                          </Icon>
                          <MDTypography variant="body2" color="text">
                            Inspected: {formatDate(property.inspectionFindings.inspectedAt)}
                          </MDTypography>
                        </MDBox>
                      )}
                    </MDBox>

                    {/* Inspection Summary */}
                    {property.inspectionFindings && (
                      <MDBox p={2} borderRadius={1} bgcolor="info.light" mb={2}>
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          color="info.dark"
                          display="block"
                          mb={0.5}
                        >
                          Inspection Summary
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          {property.inspectionFindings.amenitiesVerified?.length || 0} amenities
                          verified
                          {property.inspectionFindings.missingAmenities?.length > 0 && (
                            <span>
                              {" "}
                              • {property.inspectionFindings.missingAmenities.length} missing
                            </span>
                          )}
                        </MDTypography>
                      </MDBox>
                    )}

                    {/* Review/Details Button */}
                    <MDBox mt="auto">
                      <MDButton
                        variant="gradient"
                        color={
                          property.status === "Approved"
                            ? "success"
                            : property.status === "Rejected"
                            ? "error"
                            : "info"
                        }
                        size="medium"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/super-admin/inspections/review/${property._id || property.id}`
                          );
                        }}
                        startIcon={
                          <Icon>
                            {property.status === "Approved" || property.status === "Rejected"
                              ? "info"
                              : "visibility"}
                          </Icon>
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          py: 1.5,
                        }}
                      >
                        {property.status === "Approved" || property.status === "Rejected"
                          ? "Inspection Details"
                          : "Review Inspection"}
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </MDBox>

      {/* Inspection Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold">
              Inspection Details
            </MDTypography>
            <IconButton onClick={() => setDetailsDialogOpen(false)}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          {selectedInspection && (
            <MDBox>
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs value={detailsTab} onChange={(e, newValue) => setDetailsTab(newValue)}>
                  <Tab label="Inspection Findings" />
                  <Tab
                    label={`Photos (${
                      selectedInspection.inspectionFindings?.inspectionPhotos?.length || 0
                    })`}
                  />
                  <Tab label="Property Details" />
                </Tabs>
              </Box>

              {/* Inspection Findings Tab */}
              {detailsTab === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Card sx={{ p: 3, mb: 2 }}>
                      <MDTypography variant="h6" fontWeight="bold" mb={2}>
                        Inspection Information
                      </MDTypography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.5}
                            >
                              Inspector
                            </MDTypography>
                            <MDTypography variant="body1" fontWeight="medium">
                              {selectedInspection.assignedInspector?.fullName || "N/A"}
                            </MDTypography>
                            {selectedInspection.assignedInspector?.email && (
                              <MDTypography variant="body2" color="text.secondary">
                                {selectedInspection.assignedInspector.email}
                              </MDTypography>
                            )}
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.5}
                            >
                              Inspection Date
                            </MDTypography>
                            <MDTypography variant="body1" fontWeight="medium">
                              {formatDate(selectedInspection.inspectionDate)}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.5}
                            >
                              Inspection Location
                            </MDTypography>
                            <MDTypography variant="body1">
                              {selectedInspection.inspectionLocation || "N/A"}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.5}
                            >
                              Inspected At
                            </MDTypography>
                            <MDTypography variant="body1" fontWeight="medium">
                              {formatDate(selectedInspection.inspectionFindings?.inspectedAt)}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>

                  {selectedInspection.inspectionFindings && (
                    <>
                      {/* Verified Amenities */}
                      {selectedInspection.inspectionFindings.amenitiesVerified &&
                        selectedInspection.inspectionFindings.amenitiesVerified.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <Card sx={{ p: 2, height: "100%" }}>
                              <MDTypography
                                variant="subtitle2"
                                fontWeight="bold"
                                mb={1.5}
                                color="success.main"
                              >
                                Verified Amenities
                              </MDTypography>
                              <MDBox display="flex" gap={1} flexWrap="wrap">
                                {selectedInspection.inspectionFindings.amenitiesVerified.map(
                                  (amenity, index) => {
                                    const amenityLabel =
                                      typeof amenity === "object" && amenity.en
                                        ? amenity.en
                                        : typeof amenity === "string"
                                        ? amenity
                                        : "";
                                    return (
                                      <Chip
                                        key={index}
                                        label={amenityLabel}
                                        color="success"
                                        size="small"
                                        icon={<Icon>check_circle</Icon>}
                                      />
                                    );
                                  }
                                )}
                              </MDBox>
                            </Card>
                          </Grid>
                        )}

                      {/* Missing Amenities */}
                      {selectedInspection.inspectionFindings.missingAmenities &&
                        selectedInspection.inspectionFindings.missingAmenities.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <Card sx={{ p: 2, height: "100%" }}>
                              <MDTypography
                                variant="subtitle2"
                                fontWeight="bold"
                                mb={1.5}
                                color="error.main"
                              >
                                Missing Amenities
                              </MDTypography>
                              <MDBox display="flex" gap={1} flexWrap="wrap">
                                {selectedInspection.inspectionFindings.missingAmenities.map(
                                  (amenity, index) => {
                                    const amenityLabel =
                                      typeof amenity === "object" && amenity.en
                                        ? amenity.en
                                        : typeof amenity === "string"
                                        ? amenity
                                        : "";
                                    return (
                                      <Chip
                                        key={index}
                                        label={amenityLabel}
                                        color="error"
                                        size="small"
                                        icon={<Icon>cancel</Icon>}
                                      />
                                    );
                                  }
                                )}
                              </MDBox>
                            </Card>
                          </Grid>
                        )}

                      {/* Property Criteria Verification */}
                      {selectedInspection.criteria && selectedInspection.criteria.length > 0 && (
                        <Grid item xs={12}>
                          <Card sx={{ p: 3 }}>
                            <MDBox display="flex" alignItems="center" mb={2}>
                              <Icon sx={{ mr: 1, color: "info.main" }}>checklist</Icon>
                              <MDTypography variant="h6" fontWeight="bold">
                                Property Criteria Verification
                              </MDTypography>
                            </MDBox>
                            {Object.entries(
                              selectedInspection.criteria.reduce((acc, criterion) => {
                                const category = criterion.category || "General";
                                if (!acc[category]) acc[category] = [];
                                acc[category].push(criterion);
                                return acc;
                              }, {})
                            ).map(([category, criteria]) => (
                              <MDBox key={category} mb={3}>
                                <MDTypography
                                  variant="subtitle1"
                                  color="info.main"
                                  fontWeight="bold"
                                  display="block"
                                  mb={1.5}
                                >
                                  {category}
                                </MDTypography>
                                <Grid container spacing={2}>
                                  {criteria.map((criterion, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                      <Card
                                        sx={{
                                          p: 2,
                                          border: criterion.verified ? "2px solid" : "1px solid",
                                          borderColor: criterion.verified
                                            ? "success.main"
                                            : "grey.300",
                                          bgcolor: criterion.verified
                                            ? "rgba(76, 175, 80, 0.05)"
                                            : "grey.50",
                                          height: "100%",
                                        }}
                                      >
                                        <MDBox display="flex" alignItems="center" mb={1}>
                                          <Icon
                                            sx={{
                                              mr: 1,
                                              color: criterion.verified
                                                ? "success.main"
                                                : "grey.400",
                                              fontSize: "1.2rem",
                                            }}
                                          >
                                            {criterion.verified ? "check_circle" : "cancel"}
                                          </Icon>
                                          <MDTypography
                                            variant="body2"
                                            fontWeight="medium"
                                            color={
                                              criterion.verified ? "success.dark" : "text.secondary"
                                            }
                                          >
                                            {criterion.name}
                                          </MDTypography>
                                        </MDBox>
                                        {criterion.notes && (
                                          <MDBox
                                            mt={1.5}
                                            p={1.5}
                                            bgcolor="info.light"
                                            borderRadius={1}
                                          >
                                            <MDTypography
                                              variant="caption"
                                              fontWeight="bold"
                                              color="info.dark"
                                              display="block"
                                              mb={0.5}
                                            >
                                              Inspector Comment:
                                            </MDTypography>
                                            <MDTypography
                                              variant="caption"
                                              color="text"
                                              sx={{ whiteSpace: "pre-wrap" }}
                                            >
                                              {criterion.notes}
                                            </MDTypography>
                                          </MDBox>
                                        )}
                                        {criterion.verifiedAt && (
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            mt={1}
                                          >
                                            Verified:{" "}
                                            {new Date(criterion.verifiedAt).toLocaleDateString()}
                                          </MDTypography>
                                        )}
                                      </Card>
                                    </Grid>
                                  ))}
                                </Grid>
                              </MDBox>
                            ))}
                            <MDBox mt={2} p={2} bgcolor="info.light" borderRadius={2}>
                              <MDTypography variant="body2" fontWeight="medium" color="info.dark">
                                <Icon sx={{ verticalAlign: "middle", mr: 0.5, fontSize: "1.2rem" }}>
                                  info
                                </Icon>
                                Verified:{" "}
                                {selectedInspection.criteria.filter((c) => c.verified).length} /{" "}
                                {selectedInspection.criteria.length} criteria
                              </MDTypography>
                            </MDBox>
                          </Card>
                        </Grid>
                      )}

                      {/* Additional Notes */}
                      {selectedInspection.inspectionFindings.additionalNotes && (
                        <Grid item xs={12}>
                          <Card sx={{ p: 2 }}>
                            <MDTypography variant="subtitle2" fontWeight="bold" mb={1.5}>
                              Inspector Notes
                            </MDTypography>
                            <MDTypography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                              {selectedInspection.inspectionFindings.additionalNotes}
                            </MDTypography>
                          </Card>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              )}

              {/* Inspection Photos Tab */}
              {detailsTab === 1 && (
                <MDBox>
                  {!selectedInspection.inspectionFindings?.inspectionPhotos ||
                  selectedInspection.inspectionFindings.inspectionPhotos.length === 0 ? (
                    <MDBox textAlign="center" py={6}>
                      <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>image</Icon>
                      <MDTypography variant="h6" color="text.secondary" mb={1}>
                        No Photos Available
                      </MDTypography>
                      <MDTypography variant="body2" color="text.secondary">
                        No inspection photos were uploaded.
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <Grid container spacing={2}>
                      {selectedInspection.inspectionFindings.inspectionPhotos.map(
                        (photoUrl, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Paper
                              elevation={2}
                              sx={{
                                borderRadius: 2,
                                overflow: "hidden",
                                "&:hover": {
                                  boxShadow: 4,
                                },
                              }}
                            >
                              <MDBox
                                component="img"
                                src={photoUrl}
                                alt={`Inspection photo ${index + 1}`}
                                sx={{
                                  width: "100%",
                                  height: "250px",
                                  objectFit: "cover",
                                  cursor: "pointer",
                                }}
                                onClick={() => window.open(photoUrl, "_blank")}
                              />
                              <MDBox p={1.5}>
                                <MDTypography variant="caption" color="text.secondary">
                                  Photo {index + 1}
                                </MDTypography>
                              </MDBox>
                            </Paper>
                          </Grid>
                        )
                      )}
                    </Grid>
                  )}
                </MDBox>
              )}

              {/* Property Details Tab */}
              {detailsTab === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ p: 2 }}>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={2}
                        color="text.secondary"
                      >
                        Basic Information
                      </MDTypography>
                      <MDBox mb={1.5}>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Title
                        </MDTypography>
                        <MDTypography variant="body1" fontWeight="medium">
                          {selectedInspection.title || "N/A"}
                        </MDTypography>
                      </MDBox>
                      <MDBox mb={1.5}>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Type
                        </MDTypography>
                        <MDTypography variant="body1">
                          {selectedInspection.propertyType
                            ? selectedInspection.propertyType.charAt(0).toUpperCase() +
                              selectedInspection.propertyType.slice(1)
                            : "N/A"}
                        </MDTypography>
                      </MDBox>
                      <MDBox mb={1.5}>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Address
                        </MDTypography>
                        <MDTypography variant="body1">
                          {selectedInspection.address || "N/A"}
                          {selectedInspection.city && `, ${selectedInspection.city}`}
                          {selectedInspection.country && `, ${selectedInspection.country}`}
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" gap={2} flexWrap="wrap">
                        {selectedInspection.rooms && (
                          <MDBox>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.5}
                            >
                              Rooms
                            </MDTypography>
                            <MDTypography variant="body1" fontWeight="medium">
                              {selectedInspection.rooms}
                            </MDTypography>
                          </MDBox>
                        )}
                        {selectedInspection.bathrooms && (
                          <MDBox>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.5}
                            >
                              Bathrooms
                            </MDTypography>
                            <MDTypography variant="body1" fontWeight="medium">
                              {selectedInspection.bathrooms}
                            </MDTypography>
                          </MDBox>
                        )}
                        {selectedInspection.size && (
                          <MDBox>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.5}
                            >
                              Size
                            </MDTypography>
                            <MDTypography variant="body1" fontWeight="medium">
                              {selectedInspection.size} sq ft
                            </MDTypography>
                          </MDBox>
                        )}
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{ p: 2 }}>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={2}
                        color="text.secondary"
                      >
                        Owner Information
                      </MDTypography>
                      {selectedInspection.owner ? (
                        <>
                          <MDBox mb={1.5}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.5}
                            >
                              Name
                            </MDTypography>
                            <MDTypography variant="body1" fontWeight="medium">
                              {selectedInspection.owner.fullName || "N/A"}
                            </MDTypography>
                          </MDBox>
                          <MDBox mb={1.5}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.5}
                            >
                              Email
                            </MDTypography>
                            <MDTypography variant="body1">
                              {selectedInspection.owner.email || "N/A"}
                            </MDTypography>
                          </MDBox>
                          {selectedInspection.owner.phone && (
                            <MDBox>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                mb={0.5}
                              >
                                Phone
                              </MDTypography>
                              <MDTypography variant="body1">
                                {selectedInspection.owner.phone}
                              </MDTypography>
                            </MDBox>
                          )}
                        </>
                      ) : (
                        <MDTypography variant="body2" color="text.secondary">
                          Owner information not available
                        </MDTypography>
                      )}
                    </Card>
                  </Grid>

                  {selectedInspection.description && (
                    <Grid item xs={12}>
                      <Card sx={{ p: 2 }}>
                        <MDTypography
                          variant="subtitle2"
                          fontWeight="bold"
                          mb={1}
                          color="text.secondary"
                        >
                          Description
                        </MDTypography>
                        <MDTypography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {selectedInspection.description}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {selectedInspection.amenities && selectedInspection.amenities.length > 0 && (
                    <Grid item xs={12}>
                      <Card sx={{ p: 2 }}>
                        <MDTypography
                          variant="subtitle2"
                          fontWeight="bold"
                          mb={1.5}
                          color="text.secondary"
                        >
                          Listed Amenities
                        </MDTypography>
                        <MDBox display="flex" gap={1} flexWrap="wrap">
                          {selectedInspection.amenities.map((amenity, index) => {
                            const amenityLabel =
                              typeof amenity === "object" && amenity.en
                                ? amenity.en
                                : typeof amenity === "string"
                                ? amenity
                                : "";
                            return (
                              <Chip
                                key={index}
                                label={amenityLabel}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            );
                          })}
                        </MDBox>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              )}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          {selectedInspection && selectedInspection.status === "Inspection_Completed" && (
            <>
              <MDButton
                onClick={() => {
                  setDetailsDialogOpen(false);
                  handleOpenAction(selectedInspection, "reject");
                }}
                color="error"
                variant="outlined"
              >
                Reject
              </MDButton>
              <MDButton
                onClick={() => {
                  setDetailsDialogOpen(false);
                  handleOpenAction(selectedInspection, "approve");
                }}
                color="success"
                variant="gradient"
              >
                Approve
              </MDButton>
            </>
          )}
          <MDButton onClick={() => setDetailsDialogOpen(false)}>Close</MDButton>
        </DialogActions>
      </Dialog>

      {/* Approve/Reject Action Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => !submitting && setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedInspection?.status === "Second_Inspection_Completed"
            ? actionType === "approve"
              ? "Approve Second Inspection"
              : "Reject Second Inspection"
            : actionType === "approve"
            ? "Approve Property"
            : "Reject Property"}
        </DialogTitle>
        <DialogContent>
          {selectedInspection && (
            <MDBox>
              <MDBox mb={2}>
                <MDTypography variant="body2" color="text" mb={2}>
                  {selectedInspection.status === "Second_Inspection_Completed" &&
                  actionType === "approve"
                    ? "Approve this second inspection. The property will move to Photo Shooting phase."
                    : selectedInspection.status === "Second_Inspection_Completed" &&
                      actionType === "reject"
                    ? "Reject this second inspection. Please provide a reason."
                    : actionType === "approve"
                    ? "You are about to approve this property. It will be published and available for booking."
                    : "You are about to reject this property. Please provide a reason for the rejection."}
                </MDTypography>
                <MDBox p={2} borderRadius="lg" bgColor="grey.100" mb={2}>
                  <MDTypography variant="subtitle2" fontWeight="medium">
                    {selectedInspection.title?.en || selectedInspection.title}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    {selectedInspection.address?.en || selectedInspection.address},{" "}
                    {selectedInspection.city?.en || selectedInspection.city}
                  </MDTypography>
                </MDBox>
              </MDBox>

              {actionType === "approve" &&
              selectedInspection.status !== "Second_Inspection_Completed" ? (
                <MDBox>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Price per Night (EGP) *"
                        placeholder="Enter price per night"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        disabled={submitting}
                        required
                        error={price && parseFloat(price) <= 0}
                        helperText={
                          price && parseFloat(price) <= 0
                            ? "Price must be greater than 0"
                            : "Set the nightly rental price for this property"
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>attach_money</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Profit Percentage (%) *"
                        placeholder="Enter profit percentage (0-100)"
                        value={profitPercentage}
                        onChange={(e) => setProfitPercentage(e.target.value)}
                        disabled={submitting}
                        required
                        error={
                          profitPercentage &&
                          (parseFloat(profitPercentage) < 0 || parseFloat(profitPercentage) > 100)
                        }
                        helperText={
                          profitPercentage &&
                          (parseFloat(profitPercentage) < 0 || parseFloat(profitPercentage) > 100)
                            ? "Percentage must be between 0 and 100"
                            : "Percentage of revenue taken as profit by the organization"
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>percent</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    {price &&
                      profitPercentage &&
                      parseFloat(price) > 0 &&
                      parseFloat(profitPercentage) >= 0 &&
                      parseFloat(profitPercentage) <= 100 && (
                        <Grid item xs={12}>
                          <MDBox p={2} borderRadius={1} bgcolor="info.light">
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color="info.dark"
                              display="block"
                              mb={1}
                            >
                              Revenue Breakdown:
                            </MDTypography>
                            <MDTypography variant="body2" color="text" mb={0.5}>
                              Total Price: <strong>{parseFloat(price).toFixed(2)} EGP</strong>
                            </MDTypography>
                            <MDTypography variant="body2" color="text" mb={0.5}>
                              Organization Profit ({profitPercentage}%):{" "}
                              <strong style={{ color: "#e91e63" }}>
                                {((parseFloat(price) * parseFloat(profitPercentage)) / 100).toFixed(
                                  2
                                )}{" "}
                                EGP
                              </strong>
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              Owner Earnings:{" "}
                              <strong style={{ color: "#4caf50" }}>
                                {(
                                  parseFloat(price) -
                                  (parseFloat(price) * parseFloat(profitPercentage)) / 100
                                ).toFixed(2)}{" "}
                                EGP
                              </strong>
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      )}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Approval Notes (Optional)"
                        placeholder="Add any notes about this approval..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        disabled={submitting}
                      />
                    </Grid>
                  </Grid>
                </MDBox>
              ) : actionType === "approve" &&
                selectedInspection.status === "Second_Inspection_Completed" ? (
                <MDTypography variant="body2" color="text.secondary">
                  No additional details required. Click Approve to move the property to Photo
                  Shooting.
                </MDTypography>
              ) : (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Rejection Reason *"
                  placeholder="Please provide a detailed reason for rejecting this property..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  disabled={submitting}
                  required
                  error={!rejectReason.trim() && rejectReason.length > 0}
                  helperText={
                    !rejectReason.trim() && rejectReason.length > 0
                      ? "Rejection reason is required"
                      : ""
                  }
                />
              )}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setActionDialogOpen(false);
              setNotes("");
              setRejectReason("");
              setPrice("");
              setProfitPercentage("");
            }}
            disabled={submitting}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSubmitAction}
            variant="gradient"
            color={actionType === "approve" ? "success" : "error"}
            disabled={
              submitting ||
              (actionType === "reject" && !rejectReason.trim()) ||
              (actionType === "approve" &&
                selectedInspection?.status !== "Second_Inspection_Completed" &&
                (!price ||
                  !profitPercentage ||
                  parseFloat(price) <= 0 ||
                  parseFloat(profitPercentage) < 0 ||
                  parseFloat(profitPercentage) > 100))
            }
          >
            {submitting
              ? actionType === "approve"
                ? "Approving..."
                : "Rejecting..."
              : actionType === "approve" &&
                selectedInspection?.status === "Second_Inspection_Completed"
              ? "Approve Second Inspection"
              : actionType === "approve"
              ? "Approve & List Property"
              : "Reject"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default SuperAdminInspections;
